import { useMelioIntl } from '@melio/ar-domain';
import { TBTFormWidget, TBTFormWidgetFields } from '@melio/form-controls';
import { Button, Group, useFormSubmissionController } from '@melio/penny';
import { forwardRef, useBoolean } from '@melio/platform-utils';
import React, { useEffect, useState } from 'react';

import { CardHolderDetails, CardHolderDetailsFormFields } from '../types';
import { CardHolderDetailsForm } from './CardHolderDetailsForm';

type CardPaymentFormProps = {
  onSubmit: (data: CardHolderDetailsFormFields) => void;
  isSaving: boolean;
  amount: number;
};

export const AddCardFundingSourceForm = forwardRef<CardPaymentFormProps>(({ onSubmit, isSaving, amount }, ref) => {
  const { onSubmissionStateChange: onCardDetailsFormStateChange, submitButtonProps: cardDetailsSubmitButtonProps } =
    useFormSubmissionController<TBTFormWidgetFields>();
  const {
    onSubmissionStateChange: onCardHolderDetailsFormStateChange,
    submitButtonProps: cardHolderDetailsSubmitButtonProps,
  } = useFormSubmissionController<CardHolderDetails>();
  const { formatCurrency, formatMessage } = useMelioIntl();

  const onClick = () => {
    cardDetailsSubmitButtonProps?.onClick();
    cardHolderDetailsSubmitButtonProps?.onClick();
  };

  const [isReady, ready] = useBoolean(false);
  const [cardDetails, setCardDetails] = useState<TBTFormWidgetFields>();
  const [cardHolderDetails, setCardHolderDetails] = useState<CardHolderDetails>();

  useEffect(() => {
    if (cardDetails && cardHolderDetails) {
      onSubmit({ ...cardDetails, ...cardHolderDetails });
    }
  }, [onSubmit, cardDetails, cardHolderDetails]);

  return (
    <Group variant="vertical" ref={ref} spacing="m">
      {isReady && (
        <CardHolderDetailsForm
          onSubmit={setCardHolderDetails}
          onSubmissionStateChange={onCardHolderDetailsFormStateChange}
          isSaving={isSaving}
          size="small"
        />
      )}
      <Group variant="vertical" spacing="l">
        <TBTFormWidget
          onSubmissionStateChange={onCardDetailsFormStateChange}
          onSubmit={setCardDetails}
          isSaving={isSaving}
          onReady={ready.on}
          size="small"
          hideCardLogos
        />
        {isReady && (
          <Button
            data-testid="add-card-submit-button"
            isDisabled={cardDetailsSubmitButtonProps?.isDisabled || cardHolderDetailsSubmitButtonProps?.isDisabled}
            isLoading={cardDetailsSubmitButtonProps?.isLoading || cardHolderDetailsSubmitButtonProps?.isLoading}
            onClick={onClick}
            size="large"
            label={formatMessage('ar.dashboard.activities.cardHolder.form.buttons.submit.text', {
              amount: formatCurrency(amount),
            })}
          />
        )}
      </Group>
    </Group>
  );
});
AddCardFundingSourceForm.displayName = 'AddCardFundingSourceForm';
